import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import Topbar from '../PulseSurvey/Topbar';

import BasicSchemeForm from '../../component/schemes/BasicSchemeForm';
import PlanDetailsManagement from '../../component/schemes/PlanDetailsManagement';
import Loader from '../../component/common/Loader';
import { Api } from '../../api';

const initialFormData = {
  companyId: '',
  startDate: '',
  expiryDate: '',
  billingAmount: 0,
  seats: 0,
  providerName: '',
  providerContact: '',
  providerEmail: '',
  providerWebsite: '',
  planDetails: []
};

const AddEditCustomPlan = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const schemeId = queryParams.get('schemeId');
  const isEditParam = queryParams.get('isEdit') === 'true'; // Check if isEdit query param is true
  const companyId = localStorage.getItem('userData')
    ? JSON.parse(localStorage.getItem('userData')).companyId
    : '';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ...initialFormData,
    companyId: companyId || ''
  });
  const [errors, setErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasExistingScheme, setHasExistingScheme] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Company selection states
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearchText, setCompanySearchText] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);
  const [companyHasMore, setCompanyHasMore] = useState(true);
  const companyDropdownRef = useRef(null);

  // Load scheme data on page load
  useEffect(() => {
    if (schemeId) {
      loadSchemeData(schemeId);
    } else {
      // New scheme - set edit mode to true
      setIsEditMode(true);
      setHasExistingScheme(false);
    }
    // Fetch companies on mount
    fetchCompanies(1, '', false);
  }, [schemeId, isEditParam]);

  // Handle company search with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (isCompanyDropdownOpen) {
        fetchCompanies(1, companySearchText, false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companySearchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCompanies = async (page = 1, searchKey = '', isAppending = false) => {
    if (companyLoading) return;

    setCompanyLoading(true);
    try {
      const response = await Api.getAllCompanies(page, 10, searchKey, 'companyName', 1);

      if (response?.data?.meta?.code === 1) {
        const newCompanies = response.data.data || [];
        const totalCount = response.data.meta?.totalRecords || 0;

        if (isAppending) {
          setCompanies((prev) => [...prev, ...newCompanies]);
        } else {
          setCompanies(newCompanies);
        }

        setCompanyCurrentPage(page);

        setCompanyHasMore(
          newCompanies.length == 0 ? false : companies.length + newCompanies.length < totalCount
        );
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    setIsCompanyDropdownOpen(false);
    setCompanySearchText('');

    // Clear company error if exists
    if (errors.companyId) {
      setErrors((prev) => ({ ...prev, companyId: '' }));
    }

    // Check if company already has an active scheme
    setLoading(true);
    try {
      const response = await Api.getActiveSalarySacrificeByCompId(company._id);
      console.log(response.data.data);

      if (response?.data?.data) {
        // Company has an existing scheme - populate form with scheme data
        const schemeData = response.data.data;

        setHasExistingScheme(true);
        setIsEditMode(true); // Allow editing existing scheme
        setFormData(mapSchemeDataToForm(schemeData));
      } else {
        // No existing scheme - start fresh for this company
        setHasExistingScheme(false);
        setIsEditMode(true);
        setFormData({
          ...initialFormData,
          companyId: company._id
        });
      }
    } catch (error) {
      console.error('Error checking existing scheme:', error);
      // If error, proceed with new scheme creation
      setHasExistingScheme(false);
      setIsEditMode(true);
      setFormData({
        ...initialFormData,
        companyId: company._id
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySearch = (e) => {
    setCompanySearchText(e.target.value);
    setCompanyCurrentPage(1);
  };

  const handleCompanyScroll = useCallback(
    (e) => {
      console.log('handle scroll ');
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      console.log(bottom, companyHasMore, companyLoading);
      if (bottom && companyHasMore && !companyLoading) {
        fetchCompanies(companyCurrentPage + 1, companySearchText, true);
      }
    },
    [companyHasMore, companyLoading, companyCurrentPage, companySearchText]
  );

  // Helper function to map scheme data to form data
  const mapSchemeDataToForm = (schemeData) => {
    return {
      schemeId: schemeData.schemeId || schemeData._id,
      companyId: schemeData.companyId,
      startDate: schemeData.startDate
        ? new Date(schemeData.startDate).toISOString().split('T')[0]
        : '',
      expiryDate: schemeData.expiryDate
        ? new Date(schemeData.expiryDate).toISOString().split('T')[0]
        : '',
      billingAmount: schemeData.billingAmount || 0,
      seats: schemeData.seats || 0,
      providerName: schemeData.providerName || '',
      providerContact: schemeData.providerContact || '',
      providerEmail: schemeData.providerEmail || '',
      providerWebsite: schemeData.providerWebsite || '',
      planDetails: schemeData.planDetails || []
    };
  };

  const loadSchemeData = async (id) => {
    setLoading(true);
    try {
      const response = await Api.getSalarySchemeById(id);

      if (response?.data?.meta?.code === 1 && response?.data?.data) {
        const schemeData = response.data.data;

        setHasExistingScheme(true);
        // Set edit mode based on query parameter (isEdit=true means editable, otherwise view-only)
        setIsEditMode(isEditParam);
        setFormData(mapSchemeDataToForm(schemeData));

        // If company data is available, set the selected company
        if (schemeData.companyId && schemeData.companyName) {
          setSelectedCompany({
            _id: schemeData.companyId,
            company_name: schemeData.companyName
          });
        }
      } else {
        toast.error('Failed to load scheme data');
        navigate('/salary-sacrifice');
      }
    } catch (error) {
      console.error('Error loading scheme:', error);
      navigate('/salary-sacrifice');
    } finally {
      setLoading(false);
    }
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePlanDetailsChange = (planDetails) => {
    setFormData((prev) => ({
      ...prev,
      planDetails
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    }

    if (
      formData.startDate &&
      formData.expiryDate &&
      new Date(formData.startDate) >= new Date(formData.expiryDate)
    ) {
      newErrors.expiryDate = 'Expiry date must be after start date';
    }

    if (!formData.billingAmount || formData.billingAmount <= 0) {
      newErrors.billingAmount = 'Billing amount must be greater than 0';
    }

    if (!formData.providerName?.trim()) {
      newErrors.providerName = 'Provider name is required';
    }
    if (!formData.providerContact?.trim()) {
      newErrors.providerContact = 'Provider contact is required';
    }

    if (
      !formData.providerEmail?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.providerEmail)
    ) {
      newErrors.providerEmail = 'Please enter a valid email address';
    }

    // URL validation for website (if provided)
    if (formData.providerWebsite && !/^https?:\/\/.+\..+/.test(formData.providerWebsite)) {
      newErrors.providerWebsite = 'Please enter a valid website URL';
    }

    // Plan details validation
    if (formData.planDetails.length === 0) {
      newErrors.planDetails = 'At least one plan detail is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload
      const payload = {
        companyId: formData.companyId,
        startDate: formData.startDate,
        expiryDate: formData.expiryDate,
        billingAmount: formData.billingAmount,
        providerName: formData.providerName,
        providerContact: formData.providerContact || undefined,
        providerEmail: formData.providerEmail,
        providerWebsite: formData.providerWebsite || undefined,
        isCustomPlan: true,
        seats: 100,
        planDetails: formData.planDetails
          .filter((p) => p.title.trim() && p.description.trim())
          ?.map((detail) => ({
            title: detail.title?.trim(),
            description: detail.description?.trim(),
            icon: detail.icon
          }))
      };
      let response;
      if (formData.schemeId) {
        delete payload.companyId;
        response = await Api.UpdateCustomSalaryScheme(formData.schemeId, payload);
      } else {
        response = await Api.createCustomSalaryScheme(payload);
      }

      if (response.data?.meta?.code == 1) {
        navigate('/salary-sacrifice');
      }
    } catch (error) {
      console.error('Error saving scheme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode && hasExistingScheme) {
      // If canceling edit, reload original data
      loadSchemeData(formData.schemeId);
    }
  };

  const handlePreviewToggle = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <div className="relative">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {hasExistingScheme ? (isEditMode ? 'Edit Scheme' : 'View Scheme') : 'Create Scheme'} -
          Shoorah
        </title>
      </Helmet>

      <Topbar
        title={hasExistingScheme ? (isEditMode ? 'Edit Scheme' : 'View Scheme') : 'Create Scheme'}
        hasDesc={true}
        description="Create and manage comprehensive business schemes with detailed plan information"
        infoBtn={false}
      />

      <div className="flex flex-col w-full max-w-7xl mt-8 space-y-6">
        {/* Header with Edit/Cancel Button - Only show when scheme exists */}
        {hasExistingScheme && (
          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {isEditMode ? 'Edit Salary Sacrifice Scheme' : 'View Salary Sacrifice Scheme'}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {isEditMode
                    ? 'Update your existing salary sacrifice scheme details'
                    : 'Review your current salary sacrifice scheme configuration'}
                </p>
              </div>

              {!isEditParam && (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleEditToggle}
                    className={isEditMode ? 'btn-secondary' : 'btn-primary'}
                  >
                    {isEditMode ? 'Cancel Edit' : 'Edit Scheme'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Company Selection Section */}
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Select Company
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose the company for this salary sacrifice scheme
              </p>
            </div>

            {/* Company Dropdown */}
            <div className="relative w-full sm:w-96" ref={companyDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                disabled={hasExistingScheme && !isEditMode}
                className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 transition-colors ${
                  errors.companyId
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-2 focus:ring-shoorah-primary'
                } ${
                  hasExistingScheme && !isEditMode
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:border-gray-400 cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-blue-600 dark:text-blue-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    {selectedCompany ? (
                      <>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {selectedCompany.company_name}
                        </p>
                        {/* <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {selectedCompany.company_email || 'No email'}
                        </p> */}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Select a company...
                      </p>
                    )}
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    isCompanyDropdownOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {errors.companyId && <p className="text-red-500 text-xs mt-1">{errors.companyId}</p>}

              {/* Dropdown Menu */}
              {isCompanyDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search companies..."
                        value={companySearchText}
                        onChange={handleCompanySearch}
                        className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Company List */}
                  <div className="overflow-y-auto flex-1" onScroll={handleCompanyScroll}>
                    {companyLoading && companies.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        Loading companies...
                      </div>
                    ) : companies.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        No companies found
                      </div>
                    ) : (
                      <>
                        {companies.map((company) => (
                          <button
                            key={company._id}
                            type="button"
                            onClick={() => handleCompanySelect(company)}
                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                              selectedCompany?._id === company._id
                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                : ''
                            }`}
                          >
                            <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                              {company.company_logo ? (
                                <img
                                  src={company.company_logo}
                                  alt={`${company.company_name} logo`}
                                  className="w-10 h-10 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<svg class="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>`;
                                  }}
                                />
                              ) : (
                                <svg
                                  className="w-5 h-5 text-gray-600 dark:text-gray-300"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {company.company_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {company.company_email || 'No email'}
                              </p>
                            </div>
                            {selectedCompany?._id === company._id && (
                              <svg
                                className="w-5 h-5 text-shoorah-primary flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                        ))}
                        {companyLoading && (
                          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                            Loading more...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isPreviewMode ? (
          <>
            {/* Basic Scheme Form */}
            <BasicSchemeForm
              formData={formData}
              errors={errors}
              onChange={handleFormDataChange}
              disabled={hasExistingScheme && !isEditMode}
            />

            {/* Plan Details Management */}
            <PlanDetailsManagement
              title="Plan Details"
              planDetails={formData.planDetails}
              onChange={handlePlanDetailsChange}
              disabled={hasExistingScheme && !isEditMode}
              placeholder="No plan details added yet. Add detailed information about your scheme plans."
            />

            {/* Action Buttons - only show in edit mode */}
            {isEditMode && (
              <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      <span className="font-medium">Total Plan Details:</span>{' '}
                      {formData.planDetails.length}
                    </p>
                    {errors.planDetails && (
                      <p className="text-red-500 text-sm mt-1">{errors.planDetails}</p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={hasExistingScheme ? handleEditToggle : () => navigate(-1)}
                      className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading
                        ? hasExistingScheme
                          ? 'Updating...'
                          : 'Creating...'
                        : hasExistingScheme
                        ? 'Update Scheme'
                        : 'Create Scheme'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Preview Mode */}
            <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {formData.providerName} Scheme
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                  Company ID: {formData.companyId}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      ${formData.billingAmount}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Billing Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {formData.planDetails.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Plan Details</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {new Date(formData.startDate).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Start Date</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {Math.ceil(
                        (new Date(formData.expiryDate) - new Date(formData.startDate)) /
                          (1000 * 60 * 60 * 24)
                      )}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Days Duration</p>
                  </div>
                </div>
              </div>

              {/* Provider Information */}
              <div className="border-t pt-6 mb-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Provider Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p>
                    <span className="font-medium">Provider:</span> {formData.providerName}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span> {formData.providerEmail}
                  </p>
                  {formData.providerContact && (
                    <p>
                      <span className="font-medium">Contact:</span> {formData.providerContact}
                    </p>
                  )}
                  {formData.providerWebsite && (
                    <p>
                      <span className="font-medium">Website:</span>
                      <a
                        href={formData.providerWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-shoorah-primary hover:underline ml-1"
                      >
                        {formData.providerWebsite}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Plan Details */}
            {formData.planDetails.length > 0 && (
              <PlanDetailsManagement
                title="Plan Details"
                planDetails={formData.planDetails}
                onChange={() => {}}
                disabled={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AddEditCustomPlan;
